// @ts-check
// CLIENT same-origin `/api/*` HTTP + storage boundary (Overmind retirement, Phase
// D2). This is the former `overmind/effects.js` module, relocated OUT of Overmind
// so the section components / hooks that made same-origin `/api/*` calls (via the
// old `useEffects().http` hook or a direct `overmind/effects` import) keep working
// after Overmind is deleted. It is a pure, framework-agnostic fetch wrapper with
// ZERO Overmind coupling — moving it verbatim keeps every call site byte-identical.
//
//   - http:  the /api/* SAME-ORIGIN client. A tiny native-`fetch` wrapper (task
//            #155: dropped the client-side axios import to trim ~13-15 kB gz off
//            First Load JS). It preserves the exact axios envelope the callers
//            depend on:
//              * success (2xx)  -> resolves `{ data, status }` (`data` = parsed
//                                  JSON, or text when the body is not JSON).
//              * non-2xx / error -> REJECTS with an Error whose `.response` is
//                                  `{ data, status }`, matching axios so the
//                                  existing `e.response.status === 499` /
//                                  `e.response.data` handlers keep working.
//            Same-origin `/api/*` only.
//   - sessionStorage / localStorage:  SSR-safe (window-guarded) JSON stores. They
//            round-trip arbitrary JSON, so their value type is `any` by contract.

/**
 * @typedef {Object} HttpResponse
 * @property {any} data   Parsed JSON body (or raw text when not JSON).
 * @property {number} status HTTP status code.
 */

/**
 * Read a fetch Response body as parsed JSON, falling back to text when the body
 * is empty or not JSON — mirrors axios's default `transformResponse`.
 * @param {Response} res
 * @returns {Promise<any>}
 */
async function readBody(res) {
  const text = await res.text()
  if (!text) {
    return ''
  }
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }
  // axios auto-parses JSON even without a matching content-type; try, then fall
  // back to the raw text so string error bodies (e.g. the 499 discount message)
  // are preserved verbatim.
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/**
 * Run a same-origin `/api/*` request and resolve/reject with the axios-shaped
 * envelope. Rejects on non-2xx (axios default `validateStatus`) so downstream
 * `.catch(e => e.response.status)` handlers are behavior-preserving.
 * @param {string} url
 * @param {RequestInit} [init]
 * @returns {Promise<HttpResponse>}
 */
async function request(url, init) {
  const res = await fetch(url, init)
  const data = await readBody(res)
  if (!res.ok) {
    const err = /** @type {Error & { response: HttpResponse }} */ (
      new Error(`Request failed with status code ${res.status}`)
    )
    err.response = { data, status: res.status }
    throw err
  }
  return { data, status: res.status }
}

export const http = {
  /**
   * GET a same-origin /api/* endpoint. The resolved `.data` is backend JSON.
   * @param {string} url
   * @returns {Promise<HttpResponse>}
   */
  get(url) {
    return request(url)
  },
  /**
   * POST JSON to a same-origin /api/* endpoint.
   *
   * `headers` merges on top of the JSON content-type. The checkout money path uses
   * it to carry `Idempotency-Key`, which has to be per-call: the whole point is
   * that the SAME value goes up on a retry of the same attempt.
   * @param {string} url
   * @param {any} data
   * @param {Record<string, string>} [headers]
   * @returns {Promise<HttpResponse>}
   */
  post(url, data, headers) {
    return request(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(headers || {}) },
      body: JSON.stringify(data),
    })
  }
}

export const sessionStorage = {
  /**
   * @param {string} key
   * @param {any} data
   * @returns {void}
   */
  setItem(key, data) {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(key, JSON.stringify(data))
    }
  },
  /**
   * Returns the parsed JSON value, or `{}` on the server / when absent.
   * @param {string} key
   * @returns {any}
   */
  getItem(key) {
    if (typeof window !== 'undefined') {
      const data = /** @type {string} */ (window.sessionStorage.getItem(key))
      return JSON.parse(data)
    }
    return {}
  }
}

export const localStorage = {
  /**
   * @param {string} key
   * @param {any} data
   * @returns {void}
   */
  setItem(key, data) {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, JSON.stringify(data))
    }
  },
  /**
   * Returns the parsed JSON value, or `{}` on the server / when absent.
   * @param {string} key
   * @returns {any}
   */
  getItem(key) {
    if (typeof window !== 'undefined') {
      const data = /** @type {string} */ (window.localStorage.getItem(key))
      return JSON.parse(data)
    }
    return {}
  },
  /**
   * @param {string} key
   * @returns {void}
   */
  removeItem(key) {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(key)
    }
  }
}
